<script setup lang="ts">
import type { ReceiptScanJob } from '../internal/types.ts'
import { formatInstantDate, formatMoney } from '@taler/domain'
import { ActionButton, EmptyState, ErrorState, Spinner, StatusTag } from '@taler/ui'
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { isAiNotConfigured, isDeletable } from '../internal/job-status.ts'

defineProps<{
  jobs: readonly ReceiptScanJob[]
  isLoading: boolean
  error: Error | null
  retrying: boolean
  isDeleting: boolean
}>()

const emit = defineEmits<{ retry: []; delete: [jobId: string] }>()

const { t, locale } = useI18n()

const confirmingJobId = ref<string | undefined>(undefined)

function confirmDelete(jobId: string): void {
  emit('delete', jobId)
  confirmingJobId.value = undefined
}
</script>

<template>
  <div>
    <p v-if="isLoading" role="status" class="text-[color:var(--color-text-muted)]">
      {{ t('receiptScan.loading') }}
    </p>

    <ErrorState
      v-else-if="error"
      :title="t('receiptScan.errorTitle')"
      :body="t('receiptScan.errorBody')"
      :retry-label="t('receiptScan.retry')"
      :retrying="retrying"
      @retry="emit('retry')"
    />

    <EmptyState
      v-else-if="jobs.length === 0"
      :title="t('receiptScan.emptyTitle')"
      :body="t('receiptScan.emptyBody')"
    />

    <div v-else class="overflow-x-auto" tabindex="0" role="region" :aria-label="t('receiptScan.tableRegion')">
      <table class="w-full border-collapse bg-[color:var(--color-surface)]">
        <thead>
          <tr class="table-head">
            <th class="px-[var(--space-cell)] py-[var(--space-inline)] font-semibold" scope="col">
              {{ t('receiptScan.uploaded') }}
            </th>
            <th class="px-[var(--space-cell)] py-[var(--space-inline)] font-semibold" scope="col">
              {{ t('receiptScan.payee') }}
            </th>
            <th class="px-[var(--space-cell)] py-[var(--space-inline)] font-semibold" scope="col">
              {{ t('receiptScan.total') }}
            </th>
            <th class="px-[var(--space-cell)] py-[var(--space-inline)] font-semibold" scope="col">
              {{ t('receiptScan.status.label') }}
            </th>
            <th class="px-[var(--space-cell)] py-[var(--space-inline)] font-semibold" scope="col">
              <span class="sr-only">{{ t('receiptScan.actions') }}</span>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="job in jobs" :key="job.id" class="border-b border-[color:var(--color-border)]">
            <td
              class="whitespace-nowrap px-[var(--space-cell)] py-[var(--space-cell)] text-[color:var(--color-text)]"
            >
              {{ formatInstantDate(job.createdAt, locale as 'en' | 'hu') }}
            </td>
            <td class="px-[var(--space-cell)] py-[var(--space-cell)] text-[color:var(--color-text)]">
              {{ job.merchant ?? t('receiptScan.unknownMerchant') }}
            </td>
            <td
              class="whitespace-nowrap px-[var(--space-cell)] py-[var(--space-cell)] tabular-nums text-[color:var(--color-text)]"
            >
              {{
                job.statedTotal === undefined
                  ? '—'
                  : formatMoney(job.statedTotal, { locale: locale as 'en' | 'hu' })
              }}
            </td>
            <td class="px-[var(--space-cell)] py-[var(--space-cell)]">
              <span
                v-if="job.status === 'PENDING'"
                role="status"
                class="flex items-center gap-[var(--space-inline)] text-[color:var(--color-text-muted)]"
              >
                <Spinner />
                {{ t('receiptScan.status.PENDING') }}
              </span>
              <StatusTag v-else-if="isAiNotConfigured(job)" tone="neutral">
                {{ t('receiptScan.status.MANUAL') }}
              </StatusTag>
              <StatusTag v-else-if="job.status === 'FAILED'" tone="danger">
                {{ t('receiptScan.status.FAILED') }}
              </StatusTag>
              <StatusTag v-else-if="job.status === 'NEEDS_REVIEW'" tone="neutral">
                {{ t('receiptScan.status.NEEDS_REVIEW') }}
              </StatusTag>
              <StatusTag v-else-if="job.status === 'COMMITTED'" tone="ok">
                {{ t('receiptScan.status.COMMITTED') }}
              </StatusTag>
              <StatusTag v-else tone="neutral">{{ t('receiptScan.status.READY') }}</StatusTag>
            </td>
            <td class="px-[var(--space-cell)] py-[var(--space-cell)] text-right">
              <div class="flex items-center justify-end gap-[var(--space-inline)]">
                <RouterLink
                  :to="{ name: 'receipt-scan-detail', params: { jobId: job.id } }"
                  class="min-h-[var(--density-target-min)] rounded-[var(--radius-control)] border border-[color:var(--color-border-control)] px-3 py-2 text-[color:var(--color-text)]"
                >
                  {{ job.status === 'COMMITTED' ? t('receiptScan.view') : t('receiptScan.continue') }}
                </RouterLink>

                <template v-if="isDeletable(job)">
                  <template v-if="confirmingJobId === job.id">
                    <ActionButton variant="secondary" :disabled="isDeleting" @click="confirmDelete(job.id)">
                      {{ t('receiptScan.deleteConfirmAction') }}
                    </ActionButton>
                    <ActionButton variant="quiet" @click="confirmingJobId = undefined">
                      {{ t('receiptScan.deleteCancel') }}
                    </ActionButton>
                  </template>
                  <ActionButton v-else variant="quiet" @click="confirmingJobId = job.id">
                    {{ t('receiptScan.delete') }}
                  </ActionButton>
                </template>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>
